import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SignUpChild from './SignUpChild';
import AgeSelectionStep from './AgeSelectionStep';
import PathSelectionStep from './PathSelectionStep';
import QuestionStep from './QuestionStep';
import NeuroConsentStep from '../neuro/NeuroConsentStep';
import NeuroQuestionStep from '../neuro/NeuroQuestionStep';
import { clearQuestions } from '../../store/slices/questionSlice';
import { fetchKids } from '../../store/slices/kidSlice';
import { fetchKidNeuroStatus, clearNeuroState } from '../../store/slices/neuroSlice';

const ProfilingFlow = ({ kidId, exam = null }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState('signup');
  const [kidData, setKidData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState([]);
  const [neuroEvaluation, setNeuroEvaluation] = useState(null);

  const { kids, loading: kidsLoading } = useSelector((state) => state.kid);
  const { draft: neuroDraft } = useSelector((state) => state.neuro);

  const isDeepLink = exam === 'pedagogical' || exam === 'neuro';

  useEffect(() => {
    if (kids.length === 0) {
      dispatch(fetchKids());
    }
  }, [dispatch, kids.length]);

  const resolveStepForKid = useCallback(async (existingKid, examParam) => {
    setKidData(existingKid);

    if (!existingKid.ages_section_id) {
      setCurrentStep('age-selection');
      return;
    }

    if (examParam === 'pedagogical') {
      setCurrentStep('questions');
      return;
    }

    if (examParam === 'neuro') {
      try {
        const status = await dispatch(fetchKidNeuroStatus(existingKid.id)).unwrap();
        if (status?.draft) {
          setNeuroEvaluation(status.draft);
          setCurrentStep('neuro-questions');
          return;
        }
      } catch { /* fall through to consent */ }
      setCurrentStep('neuro-consent');
      return;
    }

    try {
      const status = await dispatch(fetchKidNeuroStatus(existingKid.id)).unwrap();
      if (status?.draft) {
        setNeuroEvaluation(status.draft);
        setCurrentStep('neuro-questions');
        return;
      }
    } catch { /* continue to path selection */ }

    setCurrentStep('path-selection');
  }, [dispatch]);

  useEffect(() => {
    if (kidId && kids.length > 0) {
      const existingKid = kids.find((kid) => kid.id === parseInt(kidId, 10));
      if (existingKid) {
        resolveStepForKid(existingKid, exam);
      }
    }
  }, [kidId, kids, exam, resolveStepForKid]);

  const { questions, loadedForAgeSectionId } = useSelector((state) => state.question);

  useEffect(() => {
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
  }, [kidData?.id]);

  const handleKidCreated = (kid) => {
    setKidData(kid);
    if (kid.ages_section_id) {
      setCurrentStep(exam === 'pedagogical' ? 'questions' : exam === 'neuro' ? 'neuro-consent' : 'path-selection');
    } else {
      setCurrentStep('age-selection');
    }
  };

  const handleAgeSelected = (updatedKid) => {
    dispatch(clearQuestions());
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setKidData(updatedKid);
    if (exam === 'pedagogical') {
      setCurrentStep('questions');
    } else if (exam === 'neuro') {
      setCurrentStep('neuro-consent');
    } else {
      setCurrentStep('path-selection');
    }
  };

  const handleNeuroBack = () => {
    navigate('/HomePage');
  };

  const handleAnswer = (questionId, answerId) => {
    setUserAnswers([...userAnswers, { question_id: questionId, answer_id: answerId }]);
  };

  const handleNextQuestion = () => {
    setCurrentQuestionIndex(currentQuestionIndex + 1);
  };

  const kidAgeSectionId = kidData?.ages_section_id != null ? String(kidData.ages_section_id) : null;
  const totalQuestionSteps =
    kidAgeSectionId && loadedForAgeSectionId === kidAgeSectionId
      ? questions.length
      : 0;

  if (kidId && kidsLoading && !kidData) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress size={48} sx={{ color: '#784e3c' }} />
        <Typography sx={{ color: '#666' }}>Chargement du profil enfant...</Typography>
      </Box>
    );
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'signup':
        return <SignUpChild onNext={handleKidCreated} />;

      case 'age-selection':
        return <AgeSelectionStep kid={kidData} onNext={handleAgeSelected} />;

      case 'path-selection':
        return (
          <PathSelectionStep
            kid={kidData}
            onSelectPedagogical={() => setCurrentStep('questions')}
            onSelectNeuro={() => setCurrentStep('neuro-consent')}
          />
        );

      case 'questions':
        return (
          <QuestionStep
            kid={kidData}
            currentStep={currentQuestionIndex}
            totalSteps={totalQuestionSteps}
            onNext={handleNextQuestion}
            onAnswer={handleAnswer}
          />
        );

      case 'neuro-consent':
        return (
          <NeuroConsentStep
            kid={kidData}
            onAccepted={(evaluation) => {
              setNeuroEvaluation(evaluation);
              setCurrentStep('neuro-questions');
            }}
            onBack={handleNeuroBack}
          />
        );

      case 'neuro-questions':
        return (
          <NeuroQuestionStep
            evaluation={neuroEvaluation || neuroDraft}
            kid={kidData}
            onComplete={(result) => {
              dispatch(clearNeuroState());
              const evalId = result.evaluation_id || result.id;
              if (isDeepLink && kidData?.id) {
                navigate(`/profile/${kidData.id}`, { state: { neuroTab: true } });
              } else {
                navigate(`/neuro/results/${evalId}`);
              }
            }}
            onBack={handleNeuroBack}
          />
        );

      default:
        return <SignUpChild onNext={handleKidCreated} />;
    }
  };

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: '#FFF7E5' }}>
      {renderCurrentStep()}
    </Box>
  );
};

export default ProfilingFlow;
